import { useEffect, useState } from 'react'

const initialSpec = () => ({
  version: 1, port: 8080,
  readiness: { scheme: 'HTTP', path: '/health', initialDelaySeconds: 5,
    periodSeconds: 10, timeoutSeconds: 3 },
  resources: { cpuRequestMillis: 100, cpuLimitMillis: 500,
    memoryRequestMi: 128, memoryLimitMi: 512 },
  secretEnv: [], volumes: [],
  exposure: { mode: 'internal', hostname: null, pathPrefix: null },
})

export default function WorkloadSettings({ service, api }) {
  const [environment, setEnvironment] = useState('development')
  const [spec, setSpec] = useState(initialSpec)
  const [revision, setRevision] = useState(0)
  const [loadedKey, setLoadedKey] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const serviceId = service?.service_id
  const selectionKey = `${serviceId || ''}:${environment}`

  useEffect(() => {
    setSpec(initialSpec()); setRevision(0); setError(''); setNotice(''); setLoadedKey('')
    if (!serviceId) return
    const controller = new AbortController()
    setBusy(true)
    api(`/services/${encodeURIComponent(serviceId)}/workload/${environment}`,
      { signal: controller.signal })
      .then(body => {
        if (!controller.signal.aborted) {
          setSpec(body.workload.spec || initialSpec())
          setRevision(body.workload.revision)
          setLoadedKey(`${serviceId}:${environment}`)
        }
      })
      .catch(cause => { if (!controller.signal.aborted) setError(cause.message) })
      .finally(() => { if (!controller.signal.aborted) setBusy(false) })
    return () => controller.abort()
  }, [serviceId, environment, api])

  if (!serviceId) return null
  const ready = loadedKey === selectionKey

  const field = (section, key, value) => setSpec(current => ({ ...current,
    [section]: { ...current[section], [key]: value } }))
  const arrayField = (section, index, key, value) => setSpec(current => ({ ...current,
    [section]: current[section].map((item, at) => at === index ? { ...item, [key]: value } : item) }))
  const add = (section, value) => setSpec(current => ({ ...current,
    [section]: [...current[section], value] }))
  const remove = (section, index) => setSpec(current => ({ ...current,
    [section]: current[section].filter((_, at) => at !== index) }))

  async function save(event) {
    event.preventDefault(); setBusy(true); setError(''); setNotice('')
    try {
      const body = await api(`/services/${encodeURIComponent(serviceId)}/workload/${environment}`,
        { method: 'PUT', body: JSON.stringify({ spec, expectedRevision: revision }) })
      setSpec(body.workload.spec); setRevision(body.workload.revision)
      setNotice('설정만 저장했습니다. 이미지 배포와 실행은 아직 하지 않았습니다.')
    } catch (cause) { setError(cause.message) }
    finally { setBusy(false) }
  }

  return <section className="deploy-panel" aria-labelledby="workload-heading">
    <h2 id="workload-heading">서비스별 실행 설정</h2>
    <p>모든 서비스에 같은 설정 형식을 사용합니다. 비밀번호 값이나 실행 명령은 입력하지 않습니다. 저장만으로 배포하지 않습니다.</p>
    <label>설정 환경 <select value={environment} onChange={event => setEnvironment(event.target.value)}>
      <option value="development">개발</option><option value="production">운영</option>
    </select></label>
    {error && <p className="deploy-error" role="alert">{error}</p>}
    {notice && <p className="deploy-notice" role="status">{notice}</p>}
    {!ready && !error && <p role="status">실행 설정을 불러오는 중입니다.</p>}
    {ready && <form className="deploy-form" onSubmit={save}>
      <label>서비스 포트 <input type="number" min="1" max="65535" value={spec.port}
        onChange={event => setSpec(current => ({ ...current, port: Number(event.target.value) }))} /></label>
      <fieldset><legend>준비 상태 검사</legend>
        <label>방식 <select value={spec.readiness.scheme}
          onChange={event => field('readiness', 'scheme', event.target.value)}>
          <option>HTTP</option><option>HTTPS</option></select></label>
        <label>경로 <input value={spec.readiness.path}
          onChange={event => field('readiness', 'path', event.target.value)} /></label>
        <label>시작 지연(초) <input type="number" min="0" max="300" value={spec.readiness.initialDelaySeconds}
          onChange={event => field('readiness', 'initialDelaySeconds', Number(event.target.value))} /></label>
        <label>검사 간격(초) <input type="number" min="2" max="60" value={spec.readiness.periodSeconds}
          onChange={event => field('readiness', 'periodSeconds', Number(event.target.value))} /></label>
        <label>응답 제한(초) <input type="number" min="1" max="10" value={spec.readiness.timeoutSeconds}
          onChange={event => field('readiness', 'timeoutSeconds', Number(event.target.value))} /></label>
      </fieldset>
      <fieldset><legend>자원 한도</legend>
        {[
          ['cpuRequestMillis', 'CPU 요청(mCPU)'], ['cpuLimitMillis', 'CPU 한도(mCPU)'],
          ['memoryRequestMi', '메모리 요청(MiB)'], ['memoryLimitMi', '메모리 한도(MiB)'],
        ].map(([key, label]) => <label key={key}>{label} <input type="number" min="1"
          value={spec.resources[key]} onChange={event => field('resources', key, Number(event.target.value))} /></label>)}
      </fieldset>
      <fieldset><legend>비밀값 참조</legend>
        <p>이미 만들어진 비밀 항목의 이름과 키만 저장하며 값은 받지 않습니다.</p>
        {spec.secretEnv.map((item, index) => <div key={index}>
          <label>환경변수명 <input value={item.envName}
            onChange={event => arrayField('secretEnv', index, 'envName', event.target.value)} /></label>
          <label>비밀 항목명 <input value={item.secretName}
            onChange={event => arrayField('secretEnv', index, 'secretName', event.target.value)} /></label>
          <label>비밀 키 <input value={item.key}
            onChange={event => arrayField('secretEnv', index, 'key', event.target.value)} /></label>
          <button type="button" onClick={() => remove('secretEnv', index)}>비밀 참조 삭제</button>
        </div>)}
        <button type="button" onClick={() => add('secretEnv', { envName: '', secretName: '', key: '' })}>비밀 참조 추가</button>
      </fieldset>
      <fieldset><legend>영구 저장소</legend>
        {spec.volumes.map((item, index) => <div key={index}>
          <label>저장소 이름 <input value={item.claimName}
            onChange={event => arrayField('volumes', index, 'claimName', event.target.value)} /></label>
          <label>연결 경로 <input value={item.mountPath}
            onChange={event => arrayField('volumes', index, 'mountPath', event.target.value)} /></label>
          <label>읽기 전용 <input type="checkbox" checked={item.readOnly}
            onChange={event => arrayField('volumes', index, 'readOnly', event.target.checked)} /></label>
          <button type="button" onClick={() => remove('volumes', index)}>저장소 삭제</button>
        </div>)}
        <button type="button" onClick={() => add('volumes',
          { claimName: '', mountPath: '', readOnly: true })}>저장소 추가</button>
      </fieldset>
      <fieldset><legend>공개 경로</legend>
        <label>공개 방식 <select value={spec.exposure.mode}
          onChange={event => setSpec(current => ({ ...current, exposure: event.target.value === 'internal'
            ? { mode: 'internal', hostname: null, pathPrefix: null }
            : { mode: 'ingress', hostname: '', pathPrefix: '/' } }))}>
          <option value="internal">내부 전용</option><option value="ingress">공개 경로</option>
        </select></label>
        {spec.exposure.mode === 'ingress' && <>
          <label>공개 호스트 <input value={spec.exposure.hostname}
            onChange={event => field('exposure', 'hostname', event.target.value)} /></label>
          <label>경로 접두어 <input value={spec.exposure.pathPrefix}
            onChange={event => field('exposure', 'pathPrefix', event.target.value)} /></label>
        </>}
      </fieldset>
      <button type="submit" disabled={busy}>{busy ? '확인 중…' : '실행 설정 저장'}</button>
      <span>설정 판본 {revision}</span>
    </form>}
  </section>
}
